import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import CameraCapture from "@/components/CameraCapture";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Loader2, ImageOff, Undo2, CheckCircle2, Plus } from "lucide-react";

const PHASE_LABEL = { INITIAL: "Kondisi Awal", FINAL: "Kondisi Akhir", RETURN: "Return" };
const MAX_DEFAULT = 5;

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit" }).replace(/\./g, ":");
  } catch { return ts; }
}

function Thumb({ photo, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let objUrl;
    api.get(`/parts/photo/${photo.id}/file`, { responseType: "blob" })
      .then((r) => { if (alive) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } }).catch(() => {});
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [photo.id]);
  return (
    <button onClick={() => onOpen(photo, url)} data-testid={`photo-thumb-${photo.phase.toLowerCase()}-${photo.id}`}
      className="relative h-12 w-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center hover:ring-2 hover:ring-[var(--gold)] transition">
      {url ? <img src={url} alt="part" className="h-full w-full object-cover" /> : <Loader2 size={14} className="animate-spin text-gray-400" />}
    </button>
  );
}

// Camera-only multi-photo (max 5) for a part-line + phase. Reports count via onCount.
export default function PartPhotos({ partLineId, phase, meta = {}, canUpload = true, compact = false, maxPhotos = MAX_DEFAULT, onCount }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [camOpen, setCamOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/parts/${partLineId}/photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos([])).finally(() => setLoading(false));
  }, [partLineId]);
  useEffect(load, [load]);

  const phasePhotos = photos.filter((p) => p.phase === phase);
  useEffect(() => { if (onCount) onCount(phasePhotos.length); }, [phasePhotos.length, onCount]);

  const onCapture = async (file) => {
    setCamOpen(false);
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("phase", phase);
      fd.append("capture_method", "CAMERA");
      await api.post(`/parts/${partLineId}/photo`, fd);
      toast.success(`Foto ${PHASE_LABEL[phase]} tersimpan`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "DUPLICATE_PHOTO") {
        const ref = detail.reference || {};
        toast.error(detail.message, { description: `Sudah dipakai: ${ref.nopol || "-"} • ${ref.part_name || "-"} • ${ref.phase || "-"} • ${fmt(ref.uploaded_at)}`, duration: 8000 });
      } else {
        toast.error(apiError(err, "Upload foto gagal"));
      }
    } finally { setUploading(false); }
  };

  const Icon = phase === "INITIAL" ? Camera : Undo2;
  const done = phasePhotos.length > 0;
  const full = phasePhotos.length >= maxPhotos;

  return (
    <div className={compact ? "flex items-center gap-1.5 flex-wrap" : "flex items-center gap-2 flex-wrap"}>
      {loading ? <Loader2 size={14} className="animate-spin text-gray-300" />
        : phasePhotos.map((p) => <Thumb key={p.id} photo={p} onOpen={(ph, u) => setPreview({ photo: ph, url: u })} />)}

      {canUpload && !full && (
        <Button size="sm" variant="outline" onClick={() => setCamOpen(true)} disabled={uploading}
          className={`h-9 ${done ? "border-emerald-300 text-emerald-700" : phase === "INITIAL" ? "border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)]" : "border-amber-300 text-amber-700"}`}
          data-testid={`photo-btn-${phase.toLowerCase()}-${partLineId}`}>
          {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : done ? <Plus size={14} className="mr-1" /> : <Icon size={14} className="mr-1" />}
          {done ? `${phasePhotos.length}/${maxPhotos}` : (compact ? (phase === "INITIAL" ? "Foto Awal" : "Foto Akhir") : `Foto ${PHASE_LABEL[phase]}`)}
        </Button>
      )}
      {canUpload && full && <span className="text-[11px] text-emerald-600 flex items-center gap-1"><CheckCircle2 size={13} /> {maxPhotos}/{maxPhotos}</span>}
      {!canUpload && phasePhotos.length === 0 && <span className="text-xs text-gray-300 flex items-center gap-1"><ImageOff size={13} /> —</span>}

      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={onCapture}
        title={`Foto ${PHASE_LABEL[phase]} — ${meta.part_name || ""}`} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><Icon size={18} className="text-[var(--gold)]" /> Foto {PHASE_LABEL[preview?.photo?.phase] || ""}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3" data-testid="photo-preview-content">
              <div className="w-full max-h-[60vh] overflow-auto rounded-lg border border-gray-200 bg-black/40 flex items-center justify-center">
                {preview.url ? <img src={preview.url} alt="preview" className="max-w-full max-h-[60vh] object-contain" /> : <Loader2 className="animate-spin text-gray-400 my-16" />}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div><span className="text-gray-400">Part:</span> <b>{preview.photo.part_name || "—"}</b></div>
                <div><span className="text-gray-400">Part No:</span> <b className="font-mono-data">{preview.photo.part_number || "—"}</b></div>
                <div><span className="text-gray-400">Nopol:</span> <b>{preview.photo.nopol || meta.nopol || "—"}</b></div>
                <div><span className="text-gray-400">Claim:</span> <b className="font-mono-data">{preview.photo.claim_number || meta.claim_number || "—"}</b></div>
                <div><span className="text-gray-400">Uploaded by:</span> <b>{preview.photo.uploaded_by || "—"}</b></div>
                <div><span className="text-gray-400">Server time:</span> <b>{fmt(preview.photo.uploaded_at_server)}</b></div>
                {preview.photo.captured_at && <div className="col-span-2"><span className="text-gray-400">Capture time (kamera):</span> {fmt(preview.photo.captured_at)}</div>}
                <div className="col-span-2 text-[11px] text-gray-400">Upload ID: {preview.photo.upload_id}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
